import { PaginationState } from '@tanstack/react-table'
import { useBoxHover } from 'features/knowledgeDocumentViewer/components/viewer/hooks/useBoxHover'
import { Box } from 'features/knowledgeDocumentViewer/components/viewer/types'
import {
  createDefaultSearchHistoryItem,
  useTabState,
} from 'features/knowledgeDocumentViewer/hooks/useTabState'
import {
  DocumentHistoryItem,
  DocumentTab,
  SearchHistoryItem,
  TabHistoryItem,
} from 'features/knowledgeDocumentViewer/types'
import React, { createContext, useContext, useEffect, useState } from 'react'
import { DocumentViewerAssetType, DocumentViewerUrlPaths } from 'types'

export enum ActionMode {
  None = 'none',
  Assign = 'assign',
  Remove = 'remove',
  Group = 'group',
  Split = 'split',
  Info = 'info',
  Link = 'link',
}

export enum TooltipOption {
  ShowTooltips = 'showTooltips',
  ShowMultiplePages = 'showMultiplePages',
  MultiPageTurner = 'multiPageTurner',
  AutoOpenNewTab = 'autoOpenNewTab',
}

export enum FilterOption {
  ShowUnconfirmed = 'showUnconfirmed',
  ShowConfirmed = 'showConfirmed',
  ShowAll = 'showAll',
  RemoveBoxes = 'removeBoxes',
}

interface KnowledgeDocumentUXContextType {
  actionMode: ActionMode
  setActionMode: (actionMode: ActionMode) => void
  selectedAssetType: DocumentViewerAssetType | undefined
  setSelectedAssetType: (assetType: DocumentViewerAssetType | undefined) => void
  groupDelimiter: string | undefined
  setGroupDelimiter: (delimiter: string | undefined) => void
  hoverId: string | null
  handleMouseEnter: (box: Box) => void
  handleMouseLeave: () => void
  openTabs: DocumentTab[]
  addTab: (tab: DocumentTab) => void
  addDocumentTab: (params: {
    documentId: string
    pageNumber?: number
    selectedBoxIds?: string[]
  }) => void
  removeTab: (tabId: string) => void
  activeTabId: string
  setActiveTabId: (tabId: string) => void
  pagination: PaginationState
  setPagination: React.Dispatch<React.SetStateAction<PaginationState>>
  activeHistoryItem: TabHistoryItem
  isOnDocument: boolean
  isOnSearch: boolean
  isOnLinkedDocuments: boolean
  currentLinkedDocumentsData: SearchHistoryItem['linkedDocuments'] | null
  currentDocumentId: string
  currentPageNumber: number
  currentSelectedBoxIds: string[]
  currentDocumentQuery: string
  currentSearchState: SearchHistoryItem['searchState']
  tooltipOptions: TooltipOption[]
  setTooltipOptions: React.Dispatch<React.SetStateAction<TooltipOption[]>>
  filterOptions: FilterOption
  setFilterOptions: React.Dispatch<React.SetStateAction<FilterOption>>
  assetTypeFilters: DocumentViewerAssetType[]
  setAssetTypeFilters: React.Dispatch<
    React.SetStateAction<DocumentViewerAssetType[]>
  >
  isAdvancedSearchOpen: boolean
  setIsAdvancedSearchOpen: React.Dispatch<React.SetStateAction<boolean>>
  handleSelectDocument: (documentId: string) => void
  isFullscreen: boolean
  isSidebarCollapsed: boolean
  setIsSidebarCollapsed: React.Dispatch<React.SetStateAction<boolean>>
  viewOnly: boolean
  pushToHistory: (tabId: string, item: TabHistoryItem) => void
  goBack: (tabId: string) => void
  goForward: (tabId: string) => void
  updateDocumentHistoryItem: (
    tabId: string,
    updates: Partial<Omit<DocumentHistoryItem, 'type'>>
  ) => void
  updateSearchHistoryItem: (
    tabId: string,
    searchStateUpdates: Partial<SearchHistoryItem['searchState']>
  ) => void
  canGoBack: boolean
  canGoForward: boolean
}

interface KnowledgeDocumentUXProviderProps {
  children: React.ReactNode
  basePath: DocumentViewerUrlPaths
  viewOnly: boolean
}

export const KnowledgeDocumentUXProvider: React.FC<
  KnowledgeDocumentUXProviderProps
> = ({ children, basePath, viewOnly }) => {
  const {
    openTabs,
    activeTabId,
    setActiveTabId,
    addTab,
    addDocumentTab,
    removeTab,
    activeHistoryItem,
    pushToHistory,
    goBack,
    goForward,
    updateDocumentHistoryItem,
    updateSearchHistoryItem,
    canGoBack,
    canGoForward,
  } = useTabState(basePath)

  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 200,
  })

  const [actionMode, setActionMode] = useState<ActionMode>(ActionMode.Info)
  const [tooltipOptions, setTooltipOptions] = useState<TooltipOption[]>([
    TooltipOption.ShowTooltips,
  ])
  const [selectedAssetType, setSelectedAssetType] = useState<
    DocumentViewerAssetType | undefined
  >(undefined)
  const [groupDelimiter, setGroupDelimiter] = useState<string | undefined>(
    undefined
  )
  const [filterOptions, setFilterOptions] = useState<FilterOption>(
    FilterOption.ShowConfirmed
  )
  const [assetTypeFilters, setAssetTypeFilters] = useState<
    DocumentViewerAssetType[]
  >([])
  const [isAdvancedSearchOpen, setIsAdvancedSearchOpen] =
    useState<boolean>(false)

  const [isFullscreen, setIsFullscreen] = useState(false)
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false)

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement)
    }

    document.addEventListener('fullscreenchange', handleFullscreenChange)
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange)
    }
  }, [])
  const { hoverId, handleMouseEnter, handleMouseLeave } = useBoxHover()

  const isOnDocument = activeHistoryItem.type === 'document'
  const isOnLinkedDocuments =
    activeHistoryItem.type === 'search' &&
    activeHistoryItem.linkedDocuments !== undefined
  const isOnSearch = activeHistoryItem.type === 'search' && !isOnLinkedDocuments
  const currentLinkedDocumentsData =
    activeHistoryItem.type === 'search'
      ? (activeHistoryItem.linkedDocuments ?? null)
      : null
  const currentDocumentId = isOnDocument ? activeHistoryItem.documentId : ''
  const currentPageNumber = isOnDocument ? activeHistoryItem.pageNumber : 1
  const currentSelectedBoxIds = isOnDocument
    ? activeHistoryItem.selectedBoxIds
    : []
  const currentDocumentQuery = isOnDocument ? activeHistoryItem.query : ''
  const currentSearchState =
    activeHistoryItem.type === 'search'
      ? activeHistoryItem.searchState
      : createDefaultSearchHistoryItem().searchState

  const handleSelectDocument = (documentId: string) => {
    if (
      tooltipOptions.includes(TooltipOption.AutoOpenNewTab) &&
      isOnDocument &&
      currentDocumentId !== ''
    ) {
      addDocumentTab({ documentId })
    } else {
      const newDocumentItem: DocumentHistoryItem = {
        type: 'document',
        documentId,
        pageNumber: 1,
        selectedBoxIds: [],
        query: '',
      }
      pushToHistory(activeTabId, newDocumentItem)
    }
  }

  const contextValue: KnowledgeDocumentUXContextType = {
    actionMode,
    setActionMode,
    selectedAssetType,
    setSelectedAssetType,
    groupDelimiter,
    setGroupDelimiter,
    hoverId,
    handleMouseEnter,
    handleMouseLeave,
    openTabs,
    addTab,
    addDocumentTab,
    removeTab,
    activeTabId,
    setActiveTabId,
    pagination,
    setPagination,
    activeHistoryItem,
    isOnDocument,
    isOnSearch,
    isOnLinkedDocuments,
    currentLinkedDocumentsData,
    currentDocumentId,
    currentPageNumber,
    currentSelectedBoxIds,
    currentDocumentQuery,
    currentSearchState,
    tooltipOptions,
    setTooltipOptions,
    filterOptions,
    setFilterOptions,
    assetTypeFilters,
    setAssetTypeFilters,
    isAdvancedSearchOpen,
    setIsAdvancedSearchOpen,
    handleSelectDocument,
    isFullscreen,
    isSidebarCollapsed,
    setIsSidebarCollapsed,
    viewOnly,
    pushToHistory,
    goBack,
    goForward,
    updateDocumentHistoryItem,
    updateSearchHistoryItem,
    canGoBack,
    canGoForward,
  }

  return (
    <KnowledgeDocumentUXContext.Provider value={contextValue}>
      {children}
    </KnowledgeDocumentUXContext.Provider>
  )
}

const KnowledgeDocumentUXContext = createContext<
  KnowledgeDocumentUXContextType | undefined
>(undefined)

export const useKnowledgeDocumentUXContext = () => {
  const context = useContext(KnowledgeDocumentUXContext)
  if (context === undefined) {
    throw new Error(
      'useKnowledgeDocumentUXContext must be used within a KnowledgeDocumentUXProvider'
    )
  }
  return context
}
